import { type ReactNode, useEffect } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { haptic } from '@/lib/tg'

export function Sheet({
  open, onClose, title, children,
}: {
  open: boolean
  onClose: () => void
  title?: string
  children: ReactNode
}) {
  useEffect(() => { if (open) haptic('light') }, [open])
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div className="fixed inset-0 z-40 bg-black/50"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }} onClick={onClose} />
          <motion.div className="fixed inset-x-0 bottom-0 z-50"
            initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 400, damping: 38 }}>
            <div className="bg-[var(--color-bg)] border-t border-[var(--color-line)] rounded-t-[22px]"
              style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 16px)' }}>
              <div className="flex justify-center pt-2.5 pb-1">
                <span className="h-1 w-10 rounded-full" style={{ background: 'var(--color-faint)' }} />
              </div>
              {title && (
                <div className="px-5 pt-1 pb-3 display text-[20px]">
                  {title}
                </div>
              )}
              <div className="px-4 pb-2">{children}</div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

type Tone = 'default' | 'primary' | 'danger'

function toneCls(tone: Tone) {
  if (tone === 'primary')
    return 'rounded-[6px] bg-[var(--color-accent)] text-[var(--color-on-accent)]'
  if (tone === 'danger')
    return 'rounded-[6px] border border-[var(--color-accent)] text-[var(--color-accent)] bg-transparent'
  return 'rounded-[6px] border border-[var(--color-line)] bg-[var(--color-surface)]'
}

export function SheetButton({
  children, onClick, tone = 'default', disabled,
}: {
  children: ReactNode
  onClick: () => void
  tone?: Tone
  disabled?: boolean
}) {
  return (
    <button
      disabled={disabled}
      onClick={() => { haptic('medium'); onClick() }}
      className={`tap w-full text-center py-4 display text-[15px] mb-2 disabled:opacity-50 ${toneCls(tone)}`}
    >
      {children}
    </button>
  )
}

export function SheetLink({
  href, children, tone = 'primary', onClick,
}: {
  href: string
  children: ReactNode
  tone?: Tone
  onClick?: () => void
}) {
  return (
    <a href={href} rel="noopener"
      onClick={() => { haptic('medium'); onClick?.() }}
      className={`tap w-full text-center py-4 display text-[15px] mb-2 ${toneCls(tone)}`}>
      {children}
    </a>
  )
}
